/**
 * Resolves the JWT to hand to {@code JitsiConnection} at connect time.
 *
 * Prefers the token carried on the location URL: it is freshly minted for THIS
 * room by addJitsiRoomToken, whereas the redux base/jwt value can be empty
 * (the jwt middleware races connect() on matched-call navigation and on
 * post-failure reloads) or stale (a token left over from a previous room).
 * Building the XMPP connection with a missing or wrong-room token makes
 * prosody (JWT_ALLOW_EMPTY=0) reject it with "token required", which loops
 * the reconnect forever.
 *
 * Implemented with plain string parsing: React Native's URLSearchParams is
 * incomplete and parseURLParams drags in dependencies this low-level module
 * should not need.
 *
 * @param {string} [stateJwt] - The token currently in the base/jwt state.
 * @param {URL|string} [locationURL] - The conference location URL, whose
 * search params may carry a freshly minted room token.
 * @returns {string|undefined} The token to use, or undefined when neither
 * source has one.
 */
export function resolveConnectionJwt(stateJwt, locationURL) {
    let urlJwt;
    if (locationURL) {
        try {
            const search = typeof locationURL === 'string'
                ? (locationURL.split('#')[0].split('?')[1] || '')
                : locationURL.search.replace(/^\?/, '');
            for (const pair of search.split('&')) {
                const [key, value] = pair.split('=');
                if (key === 'jwt' && value) {
                    urlJwt = decodeURIComponent(value);
                    break;
                }
            }
        }
        catch (e) {
            // Fall through to the state token below.
        }
    }
    return urlJwt || stateJwt || undefined;
}
